import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { PermissionProvider, usePermissions } from '@/contexts/PermissionContext';
import axios from 'axios';
import Login from '@/pages/Login';
import WelcomeScreen from '@/pages/WelcomeScreen';
import Dashboard from '@/pages/Dashboard';
import LandingPage from '@/pages/LandingPage';
import Threats from '@/pages/Threats';
import Assets from '@/pages/Assets';
import AssetDetail from '@/pages/AssetDetail';
import VulnerabilityHub from '@/pages/VulnerabilityHub';
import VulnerabilityDetail from '@/pages/VulnerabilityDetail';
import AIInsights from '@/pages/AIInsights';
import AIRemediation from '@/pages/AIRemediation';
import Compliance from '@/pages/Compliance';
import Integrations from '@/pages/Integrations';
import AuditLogs from '@/pages/AuditLogs';
import Administration from '@/pages/Administration';
import AssessmentCenter from '@/pages/AssessmentCenter';
import ThreatIntelligence from '@/pages/ThreatIntelligence';
import ThreatDashboard from '@/pages/ThreatDashboard';
import AttackPathVisualization from '@/pages/AttackPathVisualization';
import EASM from '@/pages/EASM';
import Reporting from '@/pages/Reporting';
import PlatformAdmin from '@/pages/PlatformAdmin';
import ExploitIntelligence from '@/pages/ExploitIntelligence';
import ITSMOrchestration from '@/pages/ITSMOrchestration';
import IdentityExposure from '@/pages/IdentityExposure';
import CrownJewels from '@/pages/CrownJewels';
import StrategicDashboard from '@/pages/StrategicDashboard';
import OperationalDashboard from '@/pages/OperationalDashboard';
import AssetDiscovery from '@/pages/AssetDiscovery';
import AssetManagement from '@/pages/AssetManagement';
import SecurityInitiatives from '@/pages/SecurityInitiatives';
import AdminRegistrations from '@/pages/AdminRegistrations';
import MFASettings from '@/pages/MFASettings';
import RaptorModule from '@/pages/RaptorModule';
import NotificationSettings from '@/pages/NotificationSettings';
import ComplianceReporting from '@/pages/ComplianceReporting';
import ThreatHuntingPlaybooks from '@/pages/ThreatHuntingPlaybooks';
import RemediationFactory from '@/pages/RemediationFactory';
import CorrelationEngine from '@/pages/CorrelationEngine';
import AutomationDashboard from '@/pages/AutomationDashboard';
import PentestProgressDashboard from '@/pages/PentestProgressDashboard';
import ReportBuilder from '@/pages/ReportBuilder';
import BASEngine from '@/pages/BASEngine';
import FinancialImpactDashboard from '@/pages/FinancialImpactDashboard';
import Layout from '@/components/Layout';
import { useWebSocket } from '@/hooks/useWebSocket';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const { isConnected, connect, disconnect } = useWebSocket();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setIsAuthenticated(true);
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  // Connect WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      disconnect();
    }
  }, [isAuthenticated, connect, disconnect]);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
    setShowWelcome(true); // Show welcome screen for new login
  };

  const handleContinueFromWelcome = () => {
    setShowWelcome(false);
  };

  const handleLogout = () => {
    disconnect();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    setShowWelcome(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading...</div>
      </div>
    );
  }

  // Protected Route wrapper component
  const ProtectedRoute = ({ children, pageId, requireAdmin = false, requirePlatformAdmin = false }) => {
    const { canAccessPage, loading: permLoading, isAdmin, isPlatformAdmin } = usePermissions();
    const location = useLocation();

    if (permLoading) {
      return (
        <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
          <div className="text-indigo-400 font-rajdhani text-xl">Checking permissions...</div>
        </div>
      );
    }

    if (requirePlatformAdmin && !isPlatformAdmin) {
      toast.error('Platform admin access required');
      return <Navigate to="/dashboard" state={{ from: location }} replace />;
    }

    if (requireAdmin && !isAdmin) {
      toast.error('Admin access required');
      return <Navigate to="/dashboard" state={{ from: location }} replace />;
    }

    if (pageId && !canAccessPage(pageId)) {
      toast.error('You do not have permission to access this page');
      return <Navigate to="/dashboard" state={{ from: location }} replace />;
    }

    return children;
  };

  // Wrapper to provide permissions context inside routes
  const AuthenticatedRoutes = ({ user, onLogout, showWelcome }) => {
    return (
      <PermissionProvider user={user}>
        <Routes>
          <Route
            path="/app"
            element={
              showWelcome ? (
                <Navigate to="/welcome" replace />
              ) : (
                <Layout user={user} onLogout={onLogout}>
                  <Dashboard />
                </Layout>
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/threats"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="threat_intel">
                  <Threats />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/assets"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="assets">
                  <Assets />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/assets/:id"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="assets">
                  <AssetDetail />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/vulnerabilities"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="vulnerabilities">
                  <VulnerabilityHub />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/vulnerability-hub"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="vulnerabilities">
                  <VulnerabilityHub />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/vulnerability/:vulnId"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="vulnerabilities">
                  <VulnerabilityDetail />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/ai-insights/:vulnerabilityId?"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="ai_insights">
                  <AIInsights />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/ai-remediation/:vulnerabilityId?"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="ai_insights">
                  <AIRemediation />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/compliance"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="compliance">
                  <Compliance />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/integrations"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="compliance">
                  <Integrations />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="administration" requireAdmin>
                  <AuditLogs />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/administration"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="administration" requireAdmin>
                  <Administration />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/platform-admin"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute requirePlatformAdmin>
                  <PlatformAdmin />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/registrations"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute requirePlatformAdmin>
                  <AdminRegistrations />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/assessment-center"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="assessments">
                  <AssessmentCenter />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/threat-intelligence"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="threat_intel">
                  <ThreatIntelligence />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/threat-dashboard"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="threat_intel">
                  <ThreatDashboard />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/exploit-intelligence"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="threat_intel">
                  <ExploitIntelligence />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/attack-paths"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="vulnerabilities">
                  <AttackPathVisualization />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/easm"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="easm">
                  <EASM />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/reporting"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="reports">
                  <Reporting />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/report-builder"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="reports">
                  <ReportBuilder />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/compliance-reporting"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="compliance">
                  <ComplianceReporting />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/itsm"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="itsm">
                  <ITSMOrchestration />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/identity-exposure"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="identity_exposure">
                  <IdentityExposure />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/crown-jewels"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="crown_jewels">
                  <CrownJewels />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/strategic-dashboard"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="dashboard">
                  <StrategicDashboard />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/operational-dashboard"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="dashboard">
                  <OperationalDashboard />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/asset-discovery"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="assets">
                  <AssetDiscovery />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/asset-management"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="assets">
                  <AssetManagement />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/security-initiatives"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="security_initiatives">
                  <SecurityInitiatives />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/mfa-settings"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="administration">
                  <MFASettings />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/secureprobe"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="raptor">
                  <RaptorModule />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/notification-settings"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="administration">
                  <NotificationSettings />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/threat-hunting-playbooks"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="threat_hunting">
                  <ThreatHuntingPlaybooks />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/remediation-factory"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="remediation_factory">
                  <RemediationFactory />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/correlation-engine"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="vulnerabilities">
                  <CorrelationEngine />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/automation"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="dashboard">
                  <AutomationDashboard />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/bas-engine"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="dashboard">
                  <BASEngine />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/pentest-progress/:pentestId?"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="dashboard">
                  <PentestProgressDashboard />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route
            path="/financial-impact"
            element={
              <Layout user={user} onLogout={onLogout}>
                <ProtectedRoute pageId="dashboard">
                  <FinancialImpactDashboard />
                </ProtectedRoute>
              </Layout>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </PermissionProvider>
    );
  };

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
          {/* Default route - redirect to login or dashboard based on auth */}
          <Route
            path="/"
            element={
              !isAuthenticated ? (
                <Navigate to="/login" replace />
              ) : showWelcome ? (
                <Navigate to="/welcome" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/login"
            element={
              !isAuthenticated ? (
                <Login onLogin={handleLogin} />
              ) : showWelcome ? (
                <Navigate to="/welcome" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/register"
            element={
              !isAuthenticated ? (
                <Login onLogin={handleLogin} defaultToRegister />
              ) : showWelcome ? (
                <Navigate to="/welcome" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/welcome"
            element={
              isAuthenticated && showWelcome ? (
                <WelcomeScreen user={user} onContinue={handleContinueFromWelcome} />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                showWelcome ? (
                  <Navigate to="/welcome" replace />
                ) : (
                  <AuthenticatedRoutes user={user} onLogout={handleLogout} showWelcome={showWelcome} />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </div>
    </ThemeProvider>
  );
}

export default App;
